"use client"

import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import Link from "next/link"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { startAuthentication } from "@simplewebauthn/browser"

export default function SignInPage() {
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const router = useRouter()
  const supabase = createClient()

  // Mostra messaggi da URL params (errori o info)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const error = params.get('error')
    const info = params.get('info')
    
    if (error) {
      setMessage(error)
    } else if (info) {
      setMessage(info)
    }
  }, [])

  const handlePasskeySignIn = async () => {
    setLoading(true)
    setMessage("")

    try {
      const optionsRes = await fetch('/api/auth/webauthn/authenticate-options', {
        method: 'GET',
      })

      if (!optionsRes.ok) {
        const error = await optionsRes.json()
        throw new Error(error.error || 'Nessuna passkey disponibile')
      }

      const options = await optionsRes.json()
      const credential = await startAuthentication({ optionsJSON: options })

      const verifyRes = await fetch('/api/auth/webauthn/authenticate-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credential)
      })

      if (!verifyRes.ok) {
        const error = await verifyRes.json()
        throw new Error(error.error || 'Autenticazione fallita')
      }

      const data = await verifyRes.json()

      if (data.access_token && data.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token
        })

        if (sessionError) {
          throw new Error("Impossibile creare la sessione")
        }
      } else {
        throw new Error("Tokens mancanti nella risposta del server")
      }

      toast.success("Accesso effettuato con successo!")
      await new Promise(resolve => setTimeout(resolve, 500))
      window.location.href = "/dashboard"
    } catch (error: any) {
      if (error.name === "NotAllowedError") {
        setMessage("Autenticazione annullata o timeout. Riprova.")
      } else {
        setMessage(error.message || "Errore durante l'autenticazione con passkey")
        toast.error(error.message || "Errore durante l'autenticazione")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSendOtp = async () => {
    if (!email) {
      toast.error("Inserisci la tua email")
      return
    }

    setLoading(true)
    setMessage("")

    try {
      // Step 1: Verifica sul server se l'utente può ricevere l'OTP
      const verifyResponse = await fetch('/api/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const verifyData = await verifyResponse.json()

      if (!verifyResponse.ok) {
        // Se l'account non esiste, reindirizza a signup
        if (verifyData.requiresSignup) {
          toast.info("Account non trovato. Ti reindirizziamo alla registrazione...")
          setTimeout(() => {
            router.push(`/auth/signup?email=${encodeURIComponent(email)}`)
          }, 1500)
          return
        }
        throw new Error(verifyData.error || "Errore durante la verifica")
      }

      // Step 2: Se autorizzato, invia l'OTP
      if (verifyData.canSendMagicLink) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: false
          }
        })

        if (error) {
          throw error
        }

        setOtpSent(true)
        toast.success("Codice inviato! Controlla la tua email.")
      }
    } catch (error: any) {
      setMessage(error.message || "Errore durante l'invio del codice")
      toast.error(error.message || "Errore durante l'invio del codice")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 8) {
      toast.error("Inserisci il codice a 8 cifre")
      return
    }

    setLoading(true)
    setMessage("")

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email'
      })

      if (error) {
        throw error
      }

      // Create or update user profile in public.users table
      if (data.user) {
        const { error: upsertError } = await supabase
          .from('users')
          .upsert({
            id: data.user.id,
            email: data.user.email,
            name: data.user.email?.split('@')[0] || 'User',
            email_verified: new Date().toISOString(),
          }, {
            onConflict: 'id'
          })

        if (upsertError) {
          console.error('Profile update error:', upsertError)
        }
      }

      toast.success("Accesso effettuato con successo!")
      await new Promise(resolve => setTimeout(resolve, 500))
      window.location.href = "/dashboard"
    } catch (error: any) {
      setMessage(error.message || "Codice non valido o scaduto")
      toast.error(error.message || "Codice non valido o scaduto")
    } finally {
      setLoading(false)
    }
  }

  if (otpSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/10">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
            <h3 className="text-xl font-bold text-slate-900">Inserisci il codice</h3>
            <p className="text-slate-700">
              Ti abbiamo inviato un codice a 8 cifre a <span className="font-bold">{email}</span>
            </p>
            
            {message && (
              <div className="p-3 rounded-lg text-sm font-medium bg-gradient-to-r from-red-50 to-orange-50 text-red-800 border border-red-300">
                {message}
              </div>
            )}

            <div className="mt-6">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                placeholder="00000000"
                className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
                autoFocus
              />
            </div>

            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 8}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-purple-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifica in corso..." : "Verifica e Accedi"}
            </button>

            <div className="flex items-center justify-between mt-4 text-sm">
              <button
                onClick={() => {
                  setOtpSent(false)
                  setOtp("")
                  setMessage("")
                }}
                className="text-blue-600 hover:text-blue-700 font-bold"
              >
                ← Cambia email
              </button>
              <button
                onClick={handleSendOtp}
                disabled={loading}
                className="text-slate-500 hover:text-slate-600 font-medium"
              >
                Invia nuovo codice
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Non hai ricevuto il codice? Controlla la cartella spam.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/10">
        <div className="flex justify-center mb-4">
          <div className="bg-blue-600 rounded-xl p-2">
            <Image
              src="/logo.png"
              alt="PadelManager"
              width={48}
              height={48}
              className="w-10 h-10 sm:w-12 sm:h-12"
            />
          </div>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          PadelManager
        </h1>
        <p className="text-center text-slate-700 mb-6 sm:mb-8 font-medium text-sm sm:text-base">
          Accedi per gestire i tuoi tornei
        </p>

        {message && (
          <div className="mb-4 p-3 rounded-lg text-sm font-medium bg-gradient-to-r from-red-50 to-orange-50 text-red-800 border border-red-300 shadow-sm">
            {message}
          </div>
        )}

        {/* Passkey Sign In */}
        <div className="mb-4">
          <button
            onClick={handlePasskeySignIn}
            disabled={loading}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-purple-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            {loading ? "Caricamento..." : "Accedi con Passkey"}
          </button>
          <p className="text-xs text-slate-500 mt-2 text-center leading-tight">
            💡 Metodo più sicuro e veloce (sincronizzato su tutti i tuoi dispositivi)
          </p>
        </div>

        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-300"></div>
          </div>
          <div className="relative flex justify-center text-xs sm:text-sm">
            <span className="px-2 sm:px-3 bg-white/95 text-slate-600 font-medium">Oppure</span>
          </div>
        </div>

        {/* Email OTP Sign In */}
        <div className="mb-6">
          <label className="block text-xs sm:text-sm font-bold text-slate-700 mb-2">
            Accesso con Codice Email
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
              placeholder="tua@email.com"
              className="flex-1 px-3 sm:px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition text-sm sm:text-base"
            />
            <button
              onClick={handleSendOtp}
              disabled={loading || !email}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-semibold hover:from-green-500 hover:to-teal-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            >
              {loading ? "..." : "Invia Codice"}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            📧 Riceverai un codice a 8 cifre per accedere senza password
          </p>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs sm:text-sm text-slate-700 font-medium">
            Non hai un account?{" "}
            <Link href="/auth/signup" className="text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text hover:from-blue-500 hover:to-purple-500 font-bold">
              Registrati
            </Link>
          </p>
        </div>

        <p className="text-xs text-center text-slate-500 mt-6">
          Accedendo accetti i nostri Termini di Servizio e Privacy Policy
        </p>
      </div>
    </div>
  )
}
